import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2,
  RefreshCw,
  DownloadCloud,
  ExternalLink,
  Plus,
  FileDown,
  Upload,
} from 'lucide-react';
import {
  api,
  type QuotaChurch,
  type QuotaFinancialYear,
  type QuotaCuratePatch,
  type ExclusionCategory,
} from '../../api/client';
import { fmt } from '@cca/ui';
import { downloadTemplateXlsx, downloadTemplateCsv, parseUpload } from './bulkTemplate';

// Registrar's assessment-base curation screen. Per church, per year: the
// Register figures, the per-category exclusion amounts (Capital pre-filled from
// the Register; Stipend/Mission/etc. registrar-entered), include/exclude, income
// override, manual-year add. WHICH categories are actually excluded is chosen on
// the policy (Policy & Run / What-if) — here we just record the amounts.

function amountInput(value: number, onCommit: (v: number) => void) {
  return (
    <input
      className="input input-amount"
      defaultValue={value ? Math.round(value).toLocaleString('en-NZ') : ''}
      placeholder="0"
      inputMode="numeric"
      style={{ width: 80, textAlign: 'right' }}
      onBlur={(e) => {
        const raw = e.target.value.trim().replace(/,/g, '');
        const next = raw === '' ? 0 : Math.round(Number(raw));
        if (Number.isNaN(next) || next < 0) return;
        if (next !== Math.round(value)) onCommit(next);
      }}
    />
  );
}

function moneyInput(value: number | null, onCommit: (v: number | null) => void) {
  return (
    <input
      className="input input-amount"
      defaultValue={value == null ? '' : Math.round(value).toLocaleString('en-NZ')}
      placeholder="—"
      inputMode="numeric"
      style={{ width: 90, textAlign: 'right' }}
      onBlur={(e) => {
        const raw = e.target.value.trim().replace(/,/g, '');
        const next = raw === '' ? null : Math.round(Number(raw));
        if (raw !== '' && Number.isNaN(next)) return;
        onCommit(next);
      }}
    />
  );
}

// A provisional Register gross only opens for editing on 30 March of the year
// AFTER the financial year (FY2026 → 30 Mar 2027) — by then the return is due on
// the Register. Before that the cell stays a clean locked "—". Mirrors the
// worker's provisionalGrossEditable() guard.
function provisionalGrossEditable(financialYear: number): boolean {
  return new Date() >= new Date(financialYear + 1, 2, 30); // month index 2 = March
}

export function AssessmentBaseTab() {
  const [churches, setChurches] = useState<QuotaChurch[]>([]);
  const [categories, setCategories] = useState<ExclusionCategory[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [years, setYears] = useState<QuotaFinancialYear[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const loadChurches = useCallback(async () => {
    try {
      const list = await api.quotaChurches();
      setChurches(list);
      setSelected((cur) => cur ?? list[0]?.church_id ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load churches');
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      setCategories((await api.quotaCategories()).filter((c) => c.active));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load categories');
    }
  }, []);

  const loadYears = useCallback(async (churchId: string) => {
    setLoading(true);
    try {
      setYears(await api.quotaFinancials(churchId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load financials');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadChurches();
    void loadCategories();
  }, [loadChurches, loadCategories]);
  useEffect(() => {
    if (selected) void loadYears(selected);
  }, [selected, loadYears]);

  const church = useMemo(
    () => churches.find((c) => c.church_id === selected) ?? null,
    [churches, selected],
  );

  // Pin the selected church to the top of the stack; everything else keeps its
  // alphabetical order. Selecting another drops the previous one back to its
  // alpha slot and floats the new one up.
  const orderedChurches = useMemo(() => {
    if (!church) return churches;
    return [church, ...churches.filter((c) => c.church_id !== church.church_id)];
  }, [churches, church]);

  const curate = async (patch: Omit<QuotaCuratePatch, 'church_id'>) => {
    if (!selected) return;
    try {
      await api.quotaCurate({ church_id: selected, ...patch });
      await loadYears(selected);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    }
  };

  const curateAll = async (include_in_assessment: boolean) => {
    if (!selected) return;
    try {
      for (const y of years) {
        if (y.include_in_assessment === include_in_assessment) continue;
        await api.quotaCurate({
          church_id: selected,
          financial_year: y.financial_year,
          include_in_assessment,
        });
      }
      await loadYears(selected);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    }
  };

  const setAmount = async (financial_year: number, category_key: string, amount: number) => {
    if (!selected) return;
    try {
      await api.quotaSetExclusionAmount({
        church_id: selected,
        financial_year,
        category_key,
        amount,
      });
      await loadYears(selected);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    }
  };

  // Provisional Register gross — only for years the Register hasn't published yet
  // (no official figure). The next ingest overwrites it; official data can't be
  // touched here (the worker guards on source_return_id).
  const setProvisionalGross = async (financial_year: number, gross: number | null) => {
    if (!selected) return;
    try {
      await api.quotaSetProvisionalGross({ church_id: selected, financial_year, gross });
      await loadYears(selected);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'save failed';
      setError(
        msg.includes('locked_or_missing')
          ? 'That year already holds official Register data (locked) — provisional figures can only be set where the Register has none.'
          : msg.includes('not_yet_editable')
            ? 'That year isn’t open for a provisional figure yet — it opens on 30 March the year after the financial year.'
            : msg,
      );
    }
  };

  const runTool = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setError(null);
    setNotice(null);
    try {
      await fn();
      await loadChurches();
      if (selected) await loadYears(selected);
    } catch (e) {
      setError(e instanceof Error ? e.message : `${label} failed`);
    } finally {
      setBusy(null);
    }
  };

  const exportTemplate = async (format: 'xlsx' | 'csv') => {
    setBusy('export');
    setError(null);
    setNotice(null);
    try {
      const data = await api.quotaExclusionExport();
      if (format === 'xlsx') await downloadTemplateXlsx(data, categories);
      else downloadTemplateCsv(data, categories);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'export failed');
    } finally {
      setBusy(null);
    }
  };

  const importTemplate = async (file: File) => {
    setBusy('import');
    setError(null);
    setNotice(null);
    try {
      const rows = await parseUpload(file, categories);
      if (rows.length === 0) {
        setNotice('No exclusion amounts found in the file.');
        return;
      }
      const res = await api.quotaBulkSetExclusionAmounts(rows);
      setNotice(`Imported ${res.written} exclusion amount${res.written === 1 ? '' : 's'}.`);
      if (selected) await loadYears(selected);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'import failed');
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  return (
    <>
      <div className="panel-section" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div className="panel-section-title" style={{ flex: 1 }}>
          Diocesan Quota — Assessment Base
        </div>
        <button
          className="btn btn-ghost btn-sm"
          disabled={busy !== null}
          onClick={() => void runTool('sync', () => api.quotaSyncChurches())}
        >
          {busy === 'sync' ? (
            <Loader2 className="animate-spin" size={14} />
          ) : (
            <RefreshCw size={14} />
          )}
          &nbsp;Sync churches
        </button>
        <button
          className="btn btn-ghost btn-sm"
          disabled={busy !== null}
          onClick={() => void runTool('ingest', () => api.quotaIngest())}
        >
          {busy === 'ingest' ? (
            <Loader2 className="animate-spin" size={14} />
          ) : (
            <DownloadCloud size={14} />
          )}
          &nbsp;Ingest from Register
        </button>
      </div>

      {/* Bulk exclusion-data template */}
      <div
        className="panel-section"
        style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 0 }}
      >
        <span style={{ fontSize: 12, color: 'var(--text2)' }}>Bulk exclusion data:</span>
        <button
          className="btn btn-ghost btn-sm"
          disabled={busy !== null}
          onClick={() => void exportTemplate('xlsx')}
        >
          <FileDown size={14} />
          &nbsp;Template .xlsx
        </button>
        <button
          className="btn btn-ghost btn-sm"
          disabled={busy !== null}
          onClick={() => void exportTemplate('csv')}
        >
          <FileDown size={14} />
          &nbsp;.csv
        </button>
        <button
          className="btn btn-ghost btn-sm"
          disabled={busy !== null}
          onClick={() => fileInput.current?.click()}
        >
          {busy === 'import' ? (
            <Loader2 className="animate-spin" size={14} />
          ) : (
            <Upload size={14} />
          )}
          &nbsp;Import filled template
        </button>
        <input
          ref={fileInput}
          type="file"
          accept=".xlsx,.csv"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void importTemplate(f);
          }}
        />
      </div>

      {error && (
        <div className="panel-section" style={{ color: 'var(--red)', fontSize: 13 }}>
          {error}
        </div>
      )}
      {notice && (
        <div className="panel-section" style={{ color: 'var(--text2)', fontSize: 13 }}>
          {notice}
        </div>
      )}

      <div className="panel-section" style={{ display: 'flex', gap: 20 }}>
        {/* Church list */}
        <div style={{ width: 240, flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 6 }}>
            <span style={{ boxShadow: 'inset 3px 0 0 var(--amber)', paddingLeft: 4 }}>
              Amber edge
            </span>{' '}
            = last year&rsquo;s Register gross missing or provisional
          </div>
          {orderedChurches.map((c) => {
            const active = c.church_id === selected;
            // Subtle amber flag when LAST year's Register gross is still absent
            // or unofficial (provisional/manual) — the churches the registrar's
            // eye should land on. Official locked data = plain tile.
            const flag =
              c.prior_year_gross === 'missing' || c.prior_year_gross === 'manual'
                ? c.prior_year_gross
                : null;
            return (
              <button
                key={c.church_id}
                onClick={() => setSelected(c.church_id)}
                title={
                  flag === 'missing'
                    ? `FY${c.prior_year} Register gross not recorded yet`
                    : flag === 'manual'
                      ? `FY${c.prior_year} Register gross is provisional / manually entered`
                      : undefined
                }
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  marginBottom: 4,
                  borderRadius: 6,
                  border: '1px solid var(--border)',
                  background: active ? 'var(--brand-tint, #f3efe7)' : 'transparent',
                  boxShadow: flag ? 'inset 3px 0 0 var(--amber)' : undefined,
                  cursor: 'pointer',
                  fontWeight: active ? 600 : 400,
                }}
              >
                <div style={{ fontSize: 13 }}>{c.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text2)' }}>
                  {c.charity_registration_number ?? 'no registration'}
                  {c.current_quota != null && ` · current $${fmt(c.current_quota)}`}
                </div>
              </button>
            );
          })}
        </div>

        {/* Year detail */}
        <div style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
          {church && (
            <div style={{ marginBottom: 10 }}>
              {church.charity_registration_number ? (
                <a
                  href={`https://register.charities.govt.nz/Charity/${church.charity_registration_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View on the Charities Register (opens in a new tab)"
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: 'inherit',
                    textDecoration: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {church.name}
                </a>
              ) : (
                <div style={{ fontSize: 15, fontWeight: 600 }}>{church.name}</div>
              )}
              <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                Exclusion amounts are recorded here; the policy chooses which categories apply.
                {church.current_quota != null && ` · current quota $${fmt(church.current_quota)}`}
              </div>
            </div>
          )}

          {loading ? (
            <Loader2 className="animate-spin" size={18} />
          ) : (
            <QuotaYearTable
              years={years}
              categories={categories}
              onCurate={(p) => void curate(p)}
              onToggleAll={(inc) => void curateAll(inc)}
              onSetAmount={(y, k, a) => void setAmount(y, k, a)}
              onSetProvisionalGross={(y, g) => void setProvisionalGross(y, g)}
              churchHasReg={!!church?.charity_registration_number}
              onAddManual={(year, income) =>
                void runTool('manual', () =>
                  api.quotaManualYear({
                    church_id: selected as string,
                    financial_year: year,
                    income,
                  }),
                )
              }
            />
          )}
        </div>
      </div>
    </>
  );
}

function QuotaYearTable({
  years,
  categories,
  onCurate,
  onToggleAll,
  onSetAmount,
  onSetProvisionalGross,
  churchHasReg,
  onAddManual,
}: {
  years: QuotaFinancialYear[];
  categories: ExclusionCategory[];
  onCurate: (patch: Omit<QuotaCuratePatch, 'church_id'>) => void;
  onToggleAll: (include: boolean) => void;
  onSetAmount: (year: number, categoryKey: string, amount: number) => void;
  onSetProvisionalGross: (year: number, gross: number | null) => void;
  churchHasReg: boolean;
  onAddManual: (year: number, income: number) => void;
}) {
  const [newYear, setNewYear] = useState('');
  const [newIncome, setNewIncome] = useState('');
  const colCount = 4 + categories.length + 3;

  // Select-all Include state: all ticked, none, or mixed (indeterminate).
  const allIncluded = years.length > 0 && years.every((y) => y.include_in_assessment);
  const someIncluded = years.some((y) => y.include_in_assessment);
  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someIncluded && !allIncluded;
  }, [someIncluded, allIncluded]);

  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr
            style={{
              textAlign: 'right',
              color: 'var(--text2)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <th style={{ textAlign: 'left', padding: 6 }}>Year</th>
            <th style={{ padding: 6 }}>Register gross</th>
            <th style={{ padding: 6 }}>Override</th>
            {categories.map((cat) => (
              <th
                key={cat.category_key}
                style={{ padding: 6 }}
                title={cat.register_source_field ? 'Pre-filled from the Register' : undefined}
              >
                {cat.label}
              </th>
            ))}
            <th style={{ padding: 6 }}>Net*</th>
            <th style={{ textAlign: 'center', padding: 6 }}>
              <label
                style={{
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  cursor: 'pointer',
                }}
                title={allIncluded ? 'Exclude all years' : 'Include all years'}
              >
                Inc
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allIncluded}
                  disabled={years.length === 0}
                  onChange={(e) => onToggleAll(e.target.checked)}
                  style={{ margin: 0 }}
                />
              </label>
            </th>
            <th style={{ textAlign: 'left', padding: 6 }}>Note</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {years.map((y) => (
            <tr
              key={y.financial_year}
              style={{
                borderBottom: '1px solid var(--border)',
                opacity: y.include_in_assessment ? 1 : 0.5,
                textAlign: 'right',
              }}
            >
              <td style={{ textAlign: 'left', padding: 6, fontWeight: 600 }}>
                {y.financial_year}
                {y.is_manual && <span style={{ color: 'var(--blue)' }}> · manual</span>}
              </td>
              <td style={{ padding: 6 }}>
                {y.source_return_id != null ? (
                  // Official Register figure — locked (public/official data is the
                  // safety anchor; can't be edited here).
                  <span title="Official figure from the Charities Register — locked">
                    {y.total_gross_income == null ? '—' : fmt(y.total_gross_income)}
                  </span>
                ) : !provisionalGrossEditable(y.financial_year) ? (
                  // Not due yet — the return only becomes expected on 30 March of
                  // the following year. Keep it a clean, locked "—" until then so
                  // the amber never clutters the current/future year.
                  <span
                    title={`Opens for a provisional entry on 30 March ${y.financial_year + 1}, once the return is due on the Register`}
                  >
                    {y.total_gross_income == null ? '—' : fmt(y.total_gross_income)}
                  </span>
                ) : (
                  // No official figure yet, and the year is due → allow a provisional
                  // entry. Amber flags it as unofficial; the next ingest replaces it.
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    title={
                      churchHasReg
                        ? 'Provisional — the Register has not published this year to its data feed yet. Enter the figure from the Register website; it will be OVERWRITTEN automatically when official data arrives.'
                        : 'Manual figure (church has no Register registration).'
                    }
                  >
                    <input
                      className="input input-amount"
                      defaultValue={
                        y.total_gross_income == null
                          ? ''
                          : Math.round(y.total_gross_income).toLocaleString('en-NZ')
                      }
                      placeholder="—"
                      inputMode="numeric"
                      style={{
                        width: 90,
                        textAlign: 'right',
                        ...(churchHasReg
                          ? {
                              borderColor: 'var(--amber)',
                              background: 'var(--amber-lt)',
                              color: 'var(--amber)',
                            }
                          : {}),
                      }}
                      onBlur={(e) => {
                        const raw = e.target.value.trim().replace(/,/g, '');
                        const next = raw === '' ? null : Math.round(Number(raw));
                        if (raw !== '' && (Number.isNaN(next) || (next as number) < 0)) return;
                        const cur =
                          y.total_gross_income == null ? null : Math.round(y.total_gross_income);
                        if (next !== cur) onSetProvisionalGross(y.financial_year, next);
                      }}
                    />
                    {churchHasReg && y.total_gross_income != null && (
                      <span
                        aria-label="provisional"
                        style={{ color: 'var(--amber)', fontSize: 12, lineHeight: 1 }}
                      >
                        ⚠
                      </span>
                    )}
                  </span>
                )}
              </td>
              <td style={{ padding: 6 }}>
                {moneyInput(y.income_override, (v) =>
                  onCurate({ financial_year: y.financial_year, income_override: v }),
                )}
              </td>
              {categories.map((cat) => (
                <td key={cat.category_key} style={{ padding: 6 }}>
                  {amountInput(y.category_amounts[cat.category_key] ?? 0, (v) =>
                    onSetAmount(y.financial_year, cat.category_key, v),
                  )}
                </td>
              ))}
              <td style={{ padding: 6, fontWeight: 600 }}>
                {y.effective_assessable == null ? '—' : fmt(y.effective_assessable)}
              </td>
              <td style={{ textAlign: 'center', padding: 6 }}>
                <input
                  type="checkbox"
                  checked={y.include_in_assessment}
                  onChange={(e) =>
                    onCurate({
                      financial_year: y.financial_year,
                      include_in_assessment: e.target.checked,
                    })
                  }
                />
              </td>
              <td style={{ textAlign: 'left', padding: 6 }}>
                <input
                  className="input input-note"
                  defaultValue={y.note ?? ''}
                  placeholder="—"
                  style={{ width: 140 }}
                  onBlur={(e) =>
                    onCurate({ financial_year: y.financial_year, note: e.target.value || null })
                  }
                />
              </td>
              <td style={{ padding: 6 }}>
                {y.summary_url && (
                  <a
                    href={y.summary_url}
                    target="_blank"
                    rel="noreferrer"
                    title="Open on the Charities Register"
                  >
                    <ExternalLink size={13} />
                  </a>
                )}
              </td>
            </tr>
          ))}
          {years.length === 0 && (
            <tr>
              <td colSpan={colCount} style={{ padding: 12, color: 'var(--text2)', fontSize: 13 }}>
                No financial years.{' '}
                {churchHasReg
                  ? 'Run "Ingest from Register" to pull this church’s returns.'
                  : 'No registration — add a manual year below.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text2)' }}>
        *Net shown if ALL recorded exclusions applied; a run uses only the policy’s chosen
        categories + base exclusion.
      </div>
      <div style={{ marginTop: 3, fontSize: 11, color: 'var(--text2)' }}>
        <span style={{ color: 'var(--amber)' }}>Amber Register gross</span> = provisional (Register
        hasn’t published this year to its data feed yet). Enter the figure from the Register website
        to work now; it’s <strong>overwritten automatically</strong> when official data arrives. A
        year opens for a provisional entry on <strong>30 March the following year</strong> (once the
        return is due); before that, and once official data exists, it’s locked. Only the Register
        gross behaves this way — Override and exclusions are always yours.
      </div>

      {/* Add manual year — for no-reg / entangled churches (Manaia) */}
      <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text2)' }}>Add manual year:</span>
        <input
          className="input input-compact"
          placeholder="year"
          value={newYear}
          inputMode="numeric"
          style={{ width: 70 }}
          onChange={(e) => setNewYear(e.target.value)}
        />
        <input
          className="input input-amount"
          placeholder="income"
          value={newIncome}
          inputMode="numeric"
          style={{ width: 100, textAlign: 'right' }}
          onChange={(e) => setNewIncome(e.target.value)}
        />
        <button
          className="btn btn-sm btn-ghost"
          onClick={() => {
            const yr = Number(newYear);
            const inc = Number(newIncome.replace(/,/g, ''));
            if (!Number.isFinite(yr) || yr < 1900 || !Number.isFinite(inc)) return;
            onAddManual(yr, inc);
            setNewYear('');
            setNewIncome('');
          }}
        >
          <Plus size={13} />
          &nbsp;Add
        </button>
      </div>
    </div>
  );
}
